/*
 * gd-reveal — scroll reveal for the gd-* sections.
 *
 * Sections opt in with [data-gd-reveal]; the children that animate carry
 * [data-gd-reveal-item]. A photo frame nested inside an item can carry
 * [data-gd-reveal-media] to settle out of a slight scale on the item's delay.
 * Nothing here hides anything on its own: the hiding styles only apply under
 * the .gd-reveal-ready flag that gd-reveal-init sets, so a failed script
 * leaves the page fully visible.
 */
(function () {
  'use strict';

  var READY = 'gd-reveal-ready';
  var IN = 'gd-reveal--in';
  var INSTANT = 'gd-reveal--instant';
  var SECTION = '[data-gd-reveal]';
  var ITEM = '[data-gd-reveal-item]';
  var MEDIA = '[data-gd-reveal-media]';

  /* Past this many items the per-item delay decays, so a 35-block section
     still finishes in about two seconds instead of forty. */
  var STAGGER_CAP = 8;
  var STAGGER_DECAY = 0.72;

  var html = document.documentElement;

  /* No flag means reduced motion, reveals switched off, or the init snippet
     never ran. Either way there is nothing to reveal — the page is visible. */
  if (!html.classList.contains(READY)) return;

  if (!('IntersectionObserver' in window)) {
    html.classList.remove(READY);
    return;
  }

  var step = parseFloat(getComputedStyle(html).getPropertyValue('--gd-reveal-step')) || 100;
  var duration = parseFloat(getComputedStyle(html).getPropertyValue('--gd-reveal-dur')) || 900;
  var firstPass = true;

  function delayFor(index) {
    if (index <= STAGGER_CAP) return Math.round(index * step);
    var tail = index - STAGGER_CAP;
    var extra = (step * STAGGER_DECAY * (1 - Math.pow(STAGGER_DECAY, tail))) / (1 - STAGGER_DECAY);
    return Math.round(STAGGER_CAP * step + extra);
  }

  /* Only the elements belonging to this section, so nested gd-* sections keep
     their own stagger sequence instead of inheriting the outer one. */
  function ownedBy(section, selector) {
    var found = section.querySelectorAll(selector);
    var own = [];
    for (var i = 0; i < found.length; i++) {
      if (found[i].closest(SECTION) === section) own.push(found[i]);
    }
    return own;
  }

  function isNear(el, viewport) {
    var rect = el.getBoundingClientRect();
    return rect.bottom > -viewport && rect.top < viewport * 2;
  }

  function dropWillChange(event) {
    if (event.propertyName !== 'transform' && event.propertyName !== 'opacity') return;
    var el = event.currentTarget;
    el.style.willChange = '';
    el.removeEventListener('transitionend', dropWillChange);
  }

  function reveal(section, instant) {
    if (section.classList.contains(IN)) return;

    if (instant) {
      section.classList.add(INSTANT, IN);
      /* Lift the suppression once the final state is painted, so hover
         transitions inside the section behave normally afterwards. */
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          section.classList.remove(INSTANT);
        });
      });
      return;
    }

    var items = ownedBy(section, ITEM);
    var media = ownedBy(section, MEDIA);
    var viewport = window.innerHeight || 800;
    var last = 0;

    /* Measure everything before writing anything: a section can hold dozens of
       items, and interleaving reads with style writes would thrash layout. */
    var near = [];
    var i;
    for (i = 0; i < items.length; i++) near.push(isNear(items[i], viewport));
    for (i = 0; i < media.length; i++) near.push(isNear(media[i], viewport));

    for (i = 0; i < items.length; i++) {
      var delay = delayFor(i);
      if (delay > last) last = delay;
      items[i].style.setProperty('--gd-reveal-delay', delay + 'ms');

      /* will-change promotes a layer, so only hand it to elements the visitor
         is about to look at. A 35-item list would otherwise hold 35 layers at
         once, which is what makes a mid-range phone stutter. */
      if (!near[i]) continue;
      items[i].style.willChange = 'transform, opacity';
      items[i].addEventListener('transitionend', dropWillChange);
    }

    for (var m = 0; m < media.length; m++) {
      /* Elements that are both already had the hint set in the loop above. */
      if (media[m].hasAttribute('data-gd-reveal-item')) continue;
      if (!near[items.length + m]) continue;
      media[m].style.willChange = 'transform';
      media[m].addEventListener('transitionend', dropWillChange);
    }

    requestAnimationFrame(function () {
      section.classList.add(IN);
    });

    /* transitionend never fires for items inside a hidden tab panel, so strip
       the compositor hint on a timer as well. Leaving will-change on holds a
       layer per element and is exactly what makes mobile scrolling stutter. */
    setTimeout(function () {
      var all = items.concat(media);
      for (var j = 0; j < all.length; j++) {
        all[j].style.willChange = '';
        all[j].removeEventListener('transitionend', dropWillChange);
      }
    }, last + duration + 300);
  }

  var observer = new IntersectionObserver(
    function (entries) {
      var viewport = window.innerHeight || 800;

      for (var i = 0; i < entries.length; i++) {
        var entry = entries[i];
        if (!entry.isIntersecting) continue;

        /* Two ways in. Normal sections wait until 15% of themselves shows.
           Sections taller than a few screens can never reach that ratio, so
           they also fire once they fill about a third of the viewport. */
        var enough = entry.intersectionRatio >= 0.15 || entry.intersectionRect.height >= viewport * 0.35;
        if (!enough) continue;

        observer.unobserve(entry.target);

        /* Anything already on screen at load appears at once. Animating it
           would mean fading in content the visitor is already looking at. */
        reveal(entry.target, firstPass && entry.boundingClientRect.top < viewport);
      }

      firstPass = false;
    },
    { threshold: [0, 0.01, 0.05, 0.15], rootMargin: '0px 0px -5% 0px' }
  );

  /* If nothing intersects on load, the callback above never runs to clear it. */
  setTimeout(function () {
    firstPass = false;
  }, 0);

  function scan(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var sections = [];

    if (scope.matches && scope.matches(SECTION)) sections.push(scope);
    var found = scope.querySelectorAll(SECTION);
    for (var i = 0; i < found.length; i++) sections.push(found[i]);

    for (var j = 0; j < sections.length; j++) {
      var section = sections[j];
      if (section.dataset.gdRevealBound === '1') continue;
      section.dataset.gdRevealBound = '1';
      observer.observe(section);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      scan(document);
    });
  } else {
    scan(document);
  }

  /* Theme editor: a section added or re-rendered arrives as fresh markup that
     the observer has never seen, and selecting one must never leave the
     merchant looking at an invisible section. */
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (event) {
      scan(event.target);
    });

    document.addEventListener('shopify:section:select', function (event) {
      scan(event.target);
      var section = event.target.querySelector(SECTION);
      if (section) reveal(section, false);
    });

    document.addEventListener('shopify:block:select', function (event) {
      var section = event.target.closest(SECTION);
      if (section) reveal(section, false);
    });
  }
})();
